import React, { useRef, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ChevronDown from 'web-ui-kit/assets/ChevronDown';
import { surfaceM, primaryM, primaryHighlightM, grayLightM, museumGrey } from 'web-ui-kit/colors';
import { listItemTopLevelClicked, listItemSublevelClicked, listItemClicked, listItemWithButtonClicked, listItemWithIconHovered, listItemWithIconClicked, navItemWithChevronHovered, navItemWithChevronLargeClicked, navItemWithIconHovered, navItemWithIconClicked, navItemWithChevronSmallClicked, dropdownMenuSmallClicked, clickOutsideDropdownMenuSmall, primaryListItemClicked, secondaryListItemClicked } from 'web-ui-kit/events';
import { BodySmallBoldLargeStyles, BodySmallLargeStyles, BodySmallStyles } from 'web-ui-kit/typography';
import { allRoutes } from 'web-ui-kit/routes';
import { PROJECT_SUB_LABELS } from 'web-ui-kit/dataHeader';
import SecondaryButton from 'web-ui-kit/buttons/SecondaryButton';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
/**
 * Used to update the fill value/color for some SVG elements.
 */


var HOVERED_FILL_CLASS = 'hovered--fill';
var SELECTED_FILL_CLASS = 'selected--fill'; // Helper function used to generate the CSS to pass color down to the svg

var updateSVGFillColor = function updateSVGFillColor(color) {
  return "\n  > svg > .".concat(HOVERED_FILL_CLASS, " {\n    fill: ").concat(color, ";\n  }\n");
};

var updateSVGFillColorAndRotate = function updateSVGFillColorAndRotate(color) {
  var degree = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return "\n  > svg > .".concat(SELECTED_FILL_CLASS, " {\n    fill: ").concat(color, ";\n    transform: rotate(").concat(degree, "deg);\n    transform-origin: 50% 50%;\n  }\n");
};

var _templateObject, _templateObject2;

function ListItemTopLevel(_ref) {
  var dispatch = _ref.dispatch,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      label = _ref.label;
  return (
    /*#__PURE__*/
    React.createElement(Container, {
      isOpen: isOpen,
      onClick: function onClick() {
        return dispatch({
          type: listItemTopLevelClicked,
          payload: {
            label: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(Label, {
      isOpen: isOpen
    }, label),
    /*#__PURE__*/
    React.createElement(ChevronDown, {
      className: SELECTED_FILL_CLASS,
      parentStyledClass: Container
    }))
  );
}

ListItemTopLevel.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** indicates state of dropdown below the header */
  isOpen: PropTypes.bool,

  /** user facing copy */
  label: PropTypes.string.isRequired
}; // Styles

var Container = styled.li(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  height: 3.5rem;\n  width: 100%;\n\n  padding-left: 1rem;\n  padding-right: 1rem;\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  ", ";\n  ", "\n"])), surfaceM, function (props) {
  return props.isOpen && updateSVGFillColorAndRotate(primaryM, 180);
}, function (props) {
  return props.isOpen && "\n    color: ".concat(primaryM, ";\n    background-color: transparent;\n  ");
});
var Label = styled.p(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  ", ";\n  ", "\n"])), BodySmallBoldLargeStyles, function (props) {
  return props.isOpen && "\n    color: ".concat(primaryM, ";\n  ");
});

var _templateObject$1;

function Expanded(_ref) {
  var dispatch = _ref.dispatch,
      items = _ref.items,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className;
  return (
    /*#__PURE__*/
    React.createElement(Container$1, {
      onClick: function onClick(e) {
        return e.stopPropagation();
      },
      className: className
    }, React.Children.map(items, function (item) {
      return (
        /*#__PURE__*/
        React.cloneElement(item, {
          dispatch: dispatch,
          linkComponent: linkComponent
        })
      );
    }))
  );
}

Expanded.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.array.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to allow extended styling */
  className: PropTypes.string
}; // Styles

var Container$1 = styled.ul(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  background-color: ", ";\n  box-shadow: none;\n  cursor: pointer;\n  padding: 0;\n  margin: 0;\n\n  &.with-padding-bottom {\n    padding: 0 0 4rem 0;\n  }\n"])), surfaceM);

var _templateObject$2;

function Accordion(_ref) {
  var dispatch = _ref.dispatch,
      items = _ref.items,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent;

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      showExpanded = _React$useState2[0],
      setShowExpanded = _React$useState2[1];

  return (
    /*#__PURE__*/
    React.createElement(Container$2, {
      onClick: function onClick() {
        return setShowExpanded(!showExpanded);
      }
    },
    /*#__PURE__*/
    React.createElement(ListItemTopLevel, {
      dispatch: dispatch,
      label: label,
      isOpen: showExpanded
    }), showExpanded &&
    /*#__PURE__*/
    React.createElement(Expanded, {
      items: items,
      linkComponent: linkComponent,
      dispatch: dispatch
    }))
  );
}

Accordion.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.array.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func
}; // Styles

var Container$2 = styled.ul(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  cursor: pointer;\n  padding: 0;\n  margin: 0;\n"])));

var _templateObject$3, _templateObject2$1, _templateObject3;

var VARIANTS = ['one-line', 'two-line'];

function ListItem(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      _ref$isSelected = _ref.isSelected,
      isSelected = _ref$isSelected === void 0 ? false : _ref$isSelected,
      _ref$isSubLevel = _ref.isSubLevel,
      isSubLevel = _ref$isSubLevel === void 0 ? false : _ref$isSubLevel,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$projectDetails = _ref.projectDetails,
      projectDetails = _ref$projectDetails === void 0 ? {} : _ref$projectDetails,
      _ref$subLabel = _ref.subLabel,
      subLabel = _ref$subLabel === void 0 ? '' : _ref$subLabel,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'one-line' : _ref$variant;
  var isTwoLine = variant === 'two-line';
  var needsInfoFromCustomer = isTwoLine && (subLabel === PROJECT_SUB_LABELS.TELL_US_MORE || subLabel === PROJECT_SUB_LABELS.NEW_MESSAGE);
  var isUserProjectListItem = projectDetails.isUserProject || false;
  var LinkComponent = linkComponent; // Helper method to send info related to routing and analytics.

  function handleListItemClick(_ref3) {
    var event = _ref3.event,
        _ref3$route = _ref3.route,
        route = _ref3$route === void 0 ? {} : _ref3$route;
    dispatch({
      type: isSubLevel ? listItemSublevelClicked : listItemClicked,
      payload: {
        projectDetails: projectDetails,
        isUserProjectListItem: isUserProjectListItem,
        event: event,
        route: route,
        text: label
      }
    });
  } // Helper method to get content for our Link.


  function getLinkChildren() {
    var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref4$isTwoLine = _ref4.isTwoLine,
        isTwoLine = _ref4$isTwoLine === void 0 ? false : _ref4$isTwoLine;

    return isTwoLine ?
    /*#__PURE__*/
    React.createElement(React.Fragment, null, label,
    /*#__PURE__*/
    React.createElement(SubLabel, {
      isOrange: needsInfoFromCustomer,
      isSubLevel: isSubLevel
    }, subLabel)) :
    /*#__PURE__*/
    React.createElement(React.Fragment, null, label);
  }

  return (
    /*#__PURE__*/
    React.createElement(Container$3, {
      className: className,
      isSelected: isSelected,
      isSubLevel: isSubLevel,
      onClick: function onClick(event) {
        return handleListItemClick({
          event: event,
          route: allRoutes[label]
        });
      },
      variant: variant
    },
    /*#__PURE__*/
    React.createElement(LinkComponent, {
      route: allRoutes[label],
      isUserProjectListItem: isUserProjectListItem,
      projectSlug: projectDetails.projectSlug || '',
      styledLink: StyledLink
    }, getLinkChildren({
      isTwoLine: isTwoLine
    })))
  );
}

ListItem.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** indicates if current item is selected */
  isSelected: PropTypes.bool,

  /** to distinguish small style differences */
  isSubLevel: PropTypes.bool,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** extra project details */
  projectDetails: PropTypes.object,

  /** user facing copy */
  subLabel: PropTypes.string,

  /** variant for styling */
  variant: PropTypes.oneOf(VARIANTS)
}; // Styles

var STYLE_VARIANTS = {
  'one-line': {
    height: ''
  },
  'two-line': {
    height: '4.25rem'
  }
};
var Container$3 = styled.li(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  height: ", ";\n  ", ";\n  width: 100%;\n\n  border: none;\n  background-color: ", ";\n  cursor: pointer;\n  transition: all ease 0.2s;\n\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n\n  ", "\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  ", "\n\n  &:hover {\n    background-color: transparent;\n  }\n"])), function (props) {
  return props.isSubLevel ? '3.5rem' : '2.75rem';
}, function (props) {
  return props.variant ? "height: ".concat(STYLE_VARIANTS[props.variant]['height']) : '';
}, surfaceM, function (props) {
  return props.isSubLevel && "\n    padding-right: 0.75rem;\n    padding-left: 2rem;\n  ";
}, primaryM, function (props) {
  return props.isSelected && "\n    color: ".concat(primaryM, ";\n    background-color: transparent;\n  ");
});
var StyledLink = styled.a(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n\n  /** To make the entire cell clickable,\n  we want the label to fill the full width and height. */\n  height: 100%;\n  width: 100%;\n\n  padding-right: 0.75rem;\n  padding-left: 0.75rem;\n\n  text-decoration: none;\n\n  ", ";\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  &:hover {\n    color: ", ";\n  }\n"])), BodySmallBoldLargeStyles, primaryM, primaryM);
var SubLabel = styled.p(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  margin: ", ";\n  ", "\n\n  ", ";\n\n  ", "\n"])), function (props) {
  return props.isSubLevel ? '0.5rem 0 0 0' : '0';
}, function (props) {
  return !props.isSubLevel && "\n    padding-top: 6px;\n  ";
}, BodySmallLargeStyles, function (props) {
  return props.isOrange && "\n    color: ".concat(primaryHighlightM, ";\n  ");
});

var _templateObject$4, _templateObject2$2;

function ListItemWithButton(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$buttonData = _ref.buttonData,
      buttonData = _ref$buttonData === void 0 ? {
    content: '',
    subtitle: '',
    fluid: true,
    size: 'small'
  } : _ref$buttonData;
  var content = buttonData.content,
      subtitle = buttonData.subtitle,
      fluid = buttonData.fluid,
      size = buttonData.size; // Helper method to send info related to routing and analytics.

  function handleListItemClick(_ref2) {
    var event = _ref2.event;
    dispatch({
      type: listItemWithButtonClicked,
      payload: {
        event: event,
        text: content
      }
    });
  }

  return (
    /*#__PURE__*/
    React.createElement(Container$4, {
      className: className
    },
    /*#__PURE__*/
    React.createElement(Subtitle, null, subtitle),
    /*#__PURE__*/
    React.createElement(SecondaryButton, {
      labelText: content,
      dispatch: function dispatch(event) {
        return handleListItemClick({
          event: event
        });
      },
      size: size,
      fluid: fluid
    }))
  );
}

ListItemWithButton.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** button data */
  buttonData: PropTypes.shape({
    content: PropTypes.string,
    subtitle: PropTypes.string,
    size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'responsive']),
    fluid: PropTypes.bool
  }).isRequired
}; // Styles

var Container$4 = styled.li(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  height: 6.25rem;\n  width: 100%;\n  padding: 1rem 0.75rem;\n\n  border-top: 1px solid ", ";\n  background-color: ", ";\n  transition: all ease 0.2s;\n\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  &:hover {\n    background-color: transparent;\n  }\n"])), grayLightM, surfaceM, primaryM);
var Subtitle = styled.p(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  ", "\n\n  padding-bottom: 0.5rem;\n"])), BodySmallLargeStyles);

var _templateObject$5, _templateObject2$3, _templateObject3$1;

function ListItemWithIcon(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      title = _ref.title,
      Icon = _ref.Icon;
  var LinkComponent = linkComponent; // Helper method to send info related to routing and analytics.

  function handleListItemClick(_ref3) {
    var event = _ref3.event;
    dispatch({
      type: listItemWithIconClicked,
      payload: {
        event: event,
        text: label
      }
    });
  }

  return (
    /*#__PURE__*/
    React.createElement(Container$5, {
      className: className,
      onClick: function onClick(event) {
        return handleListItemClick({
          event: event,
          route: allRoutes[label]
        });
      },
      onMouseEnter: function onMouseEnter() {
        return dispatch({
          type: listItemWithIconHovered,
          payload: {
            label: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(LinkComponent, {
      route: allRoutes[label],
      styledLink: StyledLink$1
    }, Icon,
    /*#__PURE__*/
    React.createElement(Label$1, null, title)))
  );
}

ListItemWithIcon.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** the icon should be sized to fit well in the item */
  Icon: PropTypes.element.isRequired
}; // Styles

var Container$5 = styled.li(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  height: 3.25rem;\n  width: 100%;\n  padding: 1rem 0.75rem;\n\n  border-top: 1px solid ", ";\n  background-color: ", ";\n  transition: all ease 0.2s;\n\n  display: flex;\n  flex-direction: row;\n  justify-content: center;\n\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  &:hover {\n    background-color: transparent;\n  }\n"])), grayLightM, surfaceM, primaryM);
var Label$1 = styled.p(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n  ", "\n\n  &:active {\n    color: ", ";\n  }\n\n  &:hover {\n    color: ", ";\n  }\n\n  margin-left: 10px;\n"])), BodySmallLargeStyles, primaryM, primaryM);
var StyledLink$1 = styled.a(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  justify-content: center;\n\n  /** To make the entire cell clickable,\n  we want the label to fill the full width and height. */\n  height: 100%;\n  width: 100%;\n\n  text-decoration: none;\n\n  ", ";\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n\n    svg path {\n      fill: ", ";\n    }\n  }\n\n  &:hover {\n    color: ", ";\n\n    svg path {\n      fill: ", ";\n    }\n  }\n"])), BodySmallBoldLargeStyles, primaryM, primaryM, primaryM, primaryM);

var _templateObject$6;

function Dropdown(_ref) {
  var dispatch = _ref.dispatch,
      items = _ref.items,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$showListOnLeftSi = _ref.showListOnLeftSide,
      showListOnLeftSide = _ref$showListOnLeftSi === void 0 ? false : _ref$showListOnLeftSi;
  return (
    /*#__PURE__*/
    React.createElement(Container$6, {
      onClick: function onClick(e) {
        return e.stopPropagation();
      },
      showListOnLeftSide: showListOnLeftSide
    }, React.Children.map(items, function (item) {
      return (
        /*#__PURE__*/
        React.cloneElement(item, {
          dispatch: dispatch,
          linkComponent: linkComponent
        })
      );
    }))
  );
}

Dropdown.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.array.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to show dropdown list on left side instead of default right side */
  showListOnLeftSide: PropTypes.bool
}; // Styles

var Container$6 = styled.ul(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  background-color: ", ";\n  cursor: pointer;\n  padding: 0;\n  z-index: 1;\n\n  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);\n  border-radius: 4px;\n\n  /**\n  * On real mobile (not visible with devtools),\n  * ~15vh is used by the top and bottom default grey navbars.\n  * Setting max-height to 70vh to give us more breathability\n  * in case we have optional extra top banners.\n  */\n  max-height: 70vh;\n  overflow: auto;\n\n  /** to creates a new stacking context and ensures the dropdown stays on top. */\n  position: absolute;\n\n  ", "\n"])), surfaceM, function (p) {
  return p.showListOnLeftSide && "\n    left: auto;\n    right: 4px;\n    min-width: 100%;\n    white-space: nowrap;\n  ";
});

var _templateObject$7, _templateObject2$4;

function NavItemWithChevronLarge(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent;
  var LinkComponent = linkComponent; // Helper method to send info related to routing and analytics.

  function handleNavItemClick(_ref3) {
    var event = _ref3.event,
        _ref3$route = _ref3.route,
        route = _ref3$route === void 0 ? {} : _ref3$route; // Early return if non clickable (ex: "My Projects").

    if (isEmpty(route)) {
      return;
    }

    dispatch({
      type: navItemWithChevronLargeClicked,
      payload: {
        event: event,
        route: route,
        text: label
      }
    });
  }

  return (
    /*#__PURE__*/
    React.createElement(Container$7, {
      className: className,
      onClick: function onClick(event) {
        return handleNavItemClick({
          event: event,
          route: allRoutes[label]
        });
      },
      onMouseEnter: function onMouseEnter() {
        return dispatch({
          type: navItemWithChevronHovered,
          payload: {
            label: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(LinkComponent, {
      route: allRoutes[label],
      styledLink: StyledLink$2
    }, label),
    /*#__PURE__*/
    React.createElement(ChevronDown, {
      className: HOVERED_FILL_CLASS,
      parentStyledClass: Container$7
    }))
  );
}

NavItemWithChevronLarge.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func
}; // Styles

var Container$7 = styled.li(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  height: calc(4rem - 1px);\n  width: fit-content;\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  padding-left: 0.75rem;\n  padding-right: 0.75rem;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:hover {\n    ", ";\n\n    color: ", ";\n    background-color: transparent;\n\n    > a {\n      color: ", ";\n    }\n\n    > a > a {\n      color: ", ";\n    }\n  }\n"])), surfaceM, updateSVGFillColor(primaryM), primaryM, primaryM, primaryM);
var StyledLink$2 = styled.a(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n\n  /** To make the entire cell clickable,\n  we want the label to fill the full width and height. */\n  height: 100%;\n  width: 100%;\n\n  padding-right: 0.15625rem;\n  text-decoration: none;\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  &:hover {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  ", ";\n"])), primaryM, primaryM, BodySmallBoldLargeStyles);

var _templateObject$8, _templateObject2$5;

function NavItemWithIcon(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      Icon = _ref.Icon,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent;
  var LinkComponent = linkComponent; // Helper method to send info related to routing and analytics.

  function handleNavItemClick(_ref3) {
    var event = _ref3.event,
        _ref3$route = _ref3.route,
        route = _ref3$route === void 0 ? {} : _ref3$route; // Early return if non clickable.

    if (isEmpty(route)) {
      return;
    }

    dispatch({
      type: navItemWithIconClicked,
      payload: {
        event: event,
        route: route,
        text: label
      }
    });
  }

  return (
    /*#__PURE__*/
    React.createElement(Container$8, {
      className: className,
      onClick: function onClick(event) {
        return handleNavItemClick({
          event: event,
          route: allRoutes[label]
        });
      },
      onMouseEnter: function onMouseEnter() {
        return dispatch({
          type: navItemWithIconHovered,
          payload: {
            label: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(LinkComponent, {
      route: allRoutes[label],
      styledLink: StyledLink$3
    }, Icon))
  );
}

NavItemWithIcon.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** the icon should be sized to fit well in the button */
  Icon: PropTypes.element.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func
}; // Styles

var Container$8 = styled.li(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  height: calc(4rem - 1px);\n  width: fit-content;\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  padding-left: 0.75rem;\n  padding-right: 0.75rem;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:hover {\n    svg > path {\n      fill: ", ";\n    }\n\n    svg > circle {\n      stroke: ", ";\n    }\n  }\n"])), surfaceM, primaryM, primaryM);
var StyledLink$3 = styled.a(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n\n  /** To make the entire cell clickable,\n  we want the label to fill the full width and height. */\n  height: 100%;\n  width: 100%;\n\n  text-decoration: none;\n"])));

var _templateObject$9;

function DropdownMenuLarge(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      Icon = _ref.Icon,
      items = _ref.items,
      label = _ref.label,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$showHelpMenu = _ref.showHelpMenu,
      showHelpMenu = _ref$showHelpMenu === void 0 ? false : _ref$showHelpMenu;
  var containerRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showDropdown = _useState2[0],
      setShowDropdown = _useState2[1];

  var checkMouseLeave = useCallback(function (jsEvent) {
    var isMouseMoveOutsideContainer = containerRef.current && !containerRef.current.contains(jsEvent.target);

    if (isMouseMoveOutsideContainer) {
      window.removeEventListener('mousemove', checkMouseLeave);
      setShowDropdown(false);
    }
  }, [containerRef, setShowDropdown]);
  var handleMouseEnter = useCallback(function () {
    setShowDropdown(true);
    window.addEventListener('mousemove', checkMouseLeave);
  }, [checkMouseLeave, setShowDropdown]);
  var handleMouseLeave = useCallback(function () {
    window.removeEventListener('mousemove', checkMouseLeave);
    setShowDropdown(false);
  }, [checkMouseLeave, setShowDropdown]);
  var hasIcon = !!
  /*#__PURE__*/
  React.isValidElement(Icon);
  return (
    /*#__PURE__*/
    React.createElement(Container$9, {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      showListOnLeftSide: showHelpMenu,
      "data-testid": label,
      ref: containerRef
    }, showHelpMenu && hasIcon ?
    /*#__PURE__*/
    React.createElement(NavItemWithIcon, {
      className: className,
      dispatch: dispatch,
      isOpen: showDropdown,
      Icon: Icon,
      label: label,
      linkComponent: linkComponent
    }) :
    /*#__PURE__*/
    React.createElement(NavItemWithChevronLarge, {
      className: className,
      dispatch: dispatch,
      isOpen: showDropdown,
      label: label,
      linkComponent: linkComponent
    }), showDropdown &&
    /*#__PURE__*/
    React.createElement(Dropdown, {
      dispatch: dispatch,
      items: items,
      linkComponent: linkComponent,
      showListOnLeftSide: showHelpMenu
    }))
  );
}

DropdownMenuLarge.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** feature flag to show help menu */
  showHelpMenu: PropTypes.bool
}; // Styles

var Container$9 = styled.ul(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral(["\n  cursor: pointer;\n  padding: 0;\n  margin: 0;\n\n  ", "\n"])), function (p) {
  return p.showListOnLeftSide && "\n    position: relative;\n  ";
});

var _templateObject$a, _templateObject2$6;

var VARIANTS$1 = ['tablet', ''];

function NavItemWithChevronSmall(_ref) {
  var dispatch = _ref.dispatch,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      label = _ref.label,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? '' : _ref$variant;
  return (
    /*#__PURE__*/
    React.createElement(Container$a, {
      isOpen: isOpen,
      onClick: function onClick() {
        return dispatch({
          type: navItemWithChevronSmallClicked,
          payload: {
            label: label
          }
        });
      },
      variant: variant
    },
    /*#__PURE__*/
    React.createElement(Label$2, {
      isOpen: isOpen
    }, label),
    /*#__PURE__*/
    React.createElement(ChevronDown, {
      className: SELECTED_FILL_CLASS,
      parentStyledClass: Container$a
    }))
  );
}

NavItemWithChevronSmall.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** indicates state of dropdown below the header */
  isOpen: PropTypes.bool,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** variant for styling */
  variant: PropTypes.oneOf(VARIANTS$1)
}; // Styles

var STYLE_VARIANTS$1 = {
  'tablet': {
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem'
  }
};
var Container$a = styled.li(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral(["\n  height: calc(3rem - 1px);\n  width: fit-content;\n\n  padding-left: 0.5rem;\n  padding-right: 0.5rem;\n  ", ";\n  ", ";\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  ", "\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n"])), function (props) {
  return props.variant ? "padding-left: ".concat(STYLE_VARIANTS$1[props.variant]['paddingLeft']) : '';
}, function (props) {
  return props.variant ? "padding-right: ".concat(STYLE_VARIANTS$1[props.variant]['paddingRight']) : '';
}, surfaceM, function (props) {
  return props.isOpen && "\n    color: ".concat(primaryM, ";\n    background-color: transparent;\n\n    ").concat(updateSVGFillColorAndRotate(primaryM, 180), ";\n  ");
}, primaryM);
var Label$2 = styled.p(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteral(["\n  padding-right: 0.15625rem;\n\n  ", ";\n\n  ", "\n"])), BodySmallBoldLargeStyles, function (props) {
  return props.isOpen && "\n    color: ".concat(primaryM, ";\n  ");
});

var _templateObject$b;

var VARIANTS$2 = ['tablet', ''];

function DropdownMenuSmall(_ref) {
  var dispatch = _ref.dispatch,
      items = _ref.items,
      label = _ref.label,
      _ref$showDropdown = _ref.showDropdown,
      showDropdown = _ref$showDropdown === void 0 ? false : _ref$showDropdown,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      handleCloseDropdown = _ref.handleCloseDropdown,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? '' : _ref$variant;
  var node = useRef();

  var handleClickOutside = function handleClickOutside(e) {
    if (node.current.contains(e.target)) {
      // inside click
      return;
    } // outside click


    dispatch({
      type: clickOutsideDropdownMenuSmall,
      payload: {
        label: label
      }
    });
    handleCloseDropdown(label);
  };

  useEffect(function () {
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);
  return (
    /*#__PURE__*/
    React.createElement(Container$b, {
      ref: node,
      onClick: function onClick(e) {
        return dispatch({
          type: dropdownMenuSmallClicked,
          payload: {
            label: label,
            e: e
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(NavItemWithChevronSmall, {
      dispatch: dispatch,
      label: label,
      isOpen: showDropdown,
      variant: variant
    }), showDropdown &&
    /*#__PURE__*/
    React.createElement(Dropdown, {
      dispatch: dispatch,
      items: items,
      linkComponent: linkComponent
    }))
  );
}

DropdownMenuSmall.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** used for closing on outside click */
  handleCloseDropdown: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** whether or not dropdown is open */
  showDropdown: PropTypes.bool,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** variant for styling NavItemWithChevronSmall */
  variant: PropTypes.oneOf(VARIANTS$2)
}; // Styles

var Container$b = styled.ul(_templateObject$b || (_templateObject$b = _taggedTemplateLiteral(["\n  cursor: pointer;\n  padding: 0;\n  margin: 0;\n"])));

var _templateObject$c, _templateObject2$7;

function PrimaryListItem(_ref) {
  var dispatch = _ref.dispatch,
      label = _ref.label;

  var handleListItemClick = function handleListItemClick(event) {
    dispatch({
      type: primaryListItemClicked,
      payload: {
        href: get(allRoutes[label], 'destination', ''),
        event: event,
        isExternalLink: true,
        text: label
      }
    });
  };

  return (
    /*#__PURE__*/
    React.createElement(Container$c, {
      onClick: handleListItemClick
    },
    /*#__PURE__*/
    React.createElement(Label$3, null, label))
  );
}

PrimaryListItem.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired
}; // Styles

var Container$c = styled.li(_templateObject$c || (_templateObject$c = _taggedTemplateLiteral(["\n  height: 3.5rem;\n  width: 100%;\n  display: flex;\n  align-items: center;\n\n  background-color: ", ";\n  cursor: pointer;\n  padding-right: 0.75rem;\n  padding-left: 1rem;\n  transition: all ease 0.2s;\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n"])), surfaceM, primaryM);
var Label$3 = styled.p(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteral(["\n  ", "\n"])), BodySmallBoldLargeStyles);

var _templateObject$d, _templateObject2$8;

function SecondaryListItem(_ref) {
  var dispatch = _ref.dispatch,
      label = _ref.label;
  var destination = get(allRoutes[label], 'destination', '');
  var app = get(allRoutes[label], 'app', '');

  var handleListItemClick = function handleListItemClick(event) {
    dispatch({
      type: secondaryListItemClicked,
      payload: {
        href: destination,
        event: event,
        isExternalLink: true,
        route: {
          app: app
        },
        text: label
      }
    });
  };

  return (
    /*#__PURE__*/
    React.createElement(Container$d, {
      onClick: handleListItemClick
    },
    /*#__PURE__*/
    React.createElement(Label$4, null, label))
  );
}

SecondaryListItem.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired
}; // Styles

var Container$d = styled.li(_templateObject$d || (_templateObject$d = _taggedTemplateLiteral(["\n  height: 3rem;\n  width: 100%;\n\n  background-color: ", ";\n  cursor: pointer;\n  padding-right: 0.75rem;\n  padding-left: 1rem;\n  transition: all ease 0.2s;\n  display: flex;\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n"])), surfaceM, primaryM);
var Label$4 = styled.p(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteral(["\n  ", ";\n"])), BodySmallStyles);

var _templateObject$e, _templateObject2$9, _templateObject3$2, _templateObject4;

function SecondaryListItemWithIcon(_ref) {
  var _ref$showHR = _ref.showHR,
      showHR = _ref$showHR === void 0 ? false : _ref$showHR,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      Icon = _ref.Icon,
      label = _ref.label,
      title = _ref.title;
  var destination = get(allRoutes[label], 'destination', '');
  var app = get(allRoutes[label], 'app', '');

  var handleListItemClick = function handleListItemClick(event) {
    dispatch({
      type: secondaryListItemClicked,
      payload: {
        href: destination,
        event: event,
        isExternalLink: true,
        route: {
          app: app
        },
        text: label
      }
    });
  };

  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null, showHR &&
    /*#__PURE__*/
    React.createElement(HR, null),
    /*#__PURE__*/
    React.createElement(Container$e, {
      onClick: handleListItemClick,
      className: className
    },
    /*#__PURE__*/
    React.createElement(IconContainer, null, Icon),
    /*#__PURE__*/
    React.createElement(Label$5, null, title)))
  );
}

SecondaryListItemWithIcon.propTypes = {
  className: PropTypes.string.isRequired,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** the icon should be sized to fit well in the item */
  Icon: PropTypes.element.isRequired,

  /** route label */
  label: PropTypes.string.isRequired,

  /** user facing copy */
  title: PropTypes.string.isRequired
}; // Styles

var Container$e = styled.li(_templateObject$e || (_templateObject$e = _taggedTemplateLiteral(["\n  height: 3rem;\n  width: 100%;\n\n  background-color: ", ";\n  cursor: pointer;\n  padding-right: 0.75rem;\n  padding-left: 1rem;\n  transition: all ease 0.2s;\n  display: flex;\n\n  &:active {\n    color: ", ";\n    background-color: transparent;\n  }\n"])), surfaceM, primaryM);
var Label$5 = styled.p(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteral(["\n  ", ";\n"])), BodySmallStyles);
var IconContainer = styled.div(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  margin-right: 10px;\n"])));
var HR = styled.hr(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  height: 1px;\n  border: none;\n  background-color: ", ";\n  margin: 0;\n  margin-bottom: 1rem;\n"])), museumGrey);

var _templateObject$f;

function SecondaryList(_ref) {
  var dispatch = _ref.dispatch,
      items = _ref.items,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className;
  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(HR$1, null),
    /*#__PURE__*/
    React.createElement(Expanded, {
      items: items,
      dispatch: dispatch,
      className: className
    }))
  );
}

SecondaryList.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.array.isRequired,

  /** to allow extended styling */
  className: PropTypes.string
};
var HR$1 = styled.hr(_templateObject$f || (_templateObject$f = _taggedTemplateLiteral(["\n  height: 1px;\n  border: none;\n  background-color: ", ";\n  margin: 0;\n  margin-bottom: 1rem;\n"])), museumGrey);
export { Accordion, Dropdown, DropdownMenuLarge, DropdownMenuSmall, Expanded, ListItem, ListItemTopLevel, ListItemWithButton, ListItemWithIcon, PrimaryListItem, SecondaryList, SecondaryListItem, SecondaryListItemWithIcon };